import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Smartphone,
  Bug,
  FileText,
  Users,
  Settings,
  WifiOff,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';

const menuItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/devices', label: 'Dispositivos', icon: Smartphone },
  { path: '/errors', label: 'Erros & Crashes', icon: Bug },
  { path: '/logs', label: 'Logs do Sistema', icon: FileText },
  { path: '/admin', label: 'Admin', icon: Users },
  { path: '/settings', label: 'Configurações', icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const closeSidebar = useUIStore((s) => s.closeSidebar);
  const toggleCollapsed = useUIStore((s) => s.toggleCollapsed);
  const isOnline = true;

  return (
    <>
      {/* Backdrop (mobile/tablet only) */}
      <div
        onClick={closeSidebar}
        aria-hidden="true"
        className={`fixed inset-0 z-30 bg-black/40 backdrop-blur-sm transition-opacity duration-200 lg:hidden ${
          sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col justify-between border-r border-border bg-card transition-[transform,width] duration-200 ease-out
          lg:static lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-60'}
          w-64 p-3
        `}
        aria-label="Navegação principal"
      >
        <div className="flex flex-col gap-6">
          {/* Top: Logo + close (mobile) / collapse (desktop) */}
          <div className="flex items-center justify-between gap-2 px-1 py-2">
            <Link to="/" onClick={closeSidebar} className="flex items-center gap-2 min-w-0">
              <img
                src="/logo.png"
                alt="Curral Tech"
                className={`h-9 w-auto object-contain transition-all ${
                  sidebarCollapsed ? 'lg:mx-auto' : ''
                }`}
              />
            </Link>

            {/* Close button (mobile/tablet) */}
            <button
              onClick={closeSidebar}
              aria-label="Fechar menu"
              className="flex size-9 items-center justify-center rounded-lg text-text-secondary hover:bg-muted lg:hidden"
            >
              <X size={20} />
            </button>

            {/* Collapse button (desktop) */}
            <button
              onClick={toggleCollapsed}
              aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
              className={`hidden size-9 items-center justify-center rounded-lg text-text-secondary hover:bg-muted lg:flex ${
                sidebarCollapsed ? 'lg:hidden' : ''
              }`}
            >
              <ChevronsLeft size={18} />
            </button>
          </div>

          {/* Expand button when collapsed (desktop) */}
          {sidebarCollapsed && (
            <button
              onClick={toggleCollapsed}
              aria-label="Expandir menu"
              className="hidden size-9 items-center justify-center self-center rounded-lg text-text-secondary hover:bg-muted lg:flex"
            >
              <ChevronsRight size={18} />
            </button>
          )}

          {/* Navigation */}
          <nav className="flex flex-col gap-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeSidebar}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-text-title hover:bg-muted'
                  } ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon size={20} className="shrink-0" />
                  <span
                    className={`text-sm leading-none ${isActive ? 'font-semibold' : 'font-medium'} ${
                      sidebarCollapsed ? 'lg:hidden' : ''
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <div
            className={`flex items-center gap-3 px-3 py-2 ${
              sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''
            }`}
            title={sidebarCollapsed ? `Conexão: ${isOnline ? 'Online' : 'Offline'}` : undefined}
          >
            <span className="relative flex size-2.5 shrink-0">
              {isOnline ? (
                <>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-online" />
                </>
              ) : (
                <WifiOff size={12} className="text-offline" />
              )}
            </span>
            <p
              className={`text-xs font-medium leading-none text-text-secondary ${
                sidebarCollapsed ? 'lg:hidden' : ''
              }`}
            >
              {isOnline ? 'Online' : 'Offline'}
            </p>
          </div>

          <div
            className={`px-3 py-1 text-xs text-text-hint ${
              sidebarCollapsed ? 'lg:hidden' : ''
            }`}
          >
            v1.0.0
          </div>
        </div>
      </aside>
    </>
  );
}
